import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, Check } from 'lucide-react';
import type { SessionState } from '@ember/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const MAX_PLAYERS = 2;

interface LobbyCardProps {
  session: SessionState;
  onStart: () => void;
}

/**
 * Affichage du salon d'attente : code + lien partageable + liste des joueurs
 * + bouton "Lancer la partie" actif quand on est 2.
 *
 * Animation : a l'arrivee d'un joueur, sa carte fait un fade + scale "pop" subtile
 * (initial 0.95 -> 1) pour signaler visuellement le nouvel arrivant.
 */
export function LobbyCard({ session, onStart }: LobbyCardProps) {
  const [copied, setCopied] = useState(false);
  const joinUrl = `${window.location.origin}/s/${session.shortCode}`;
  const ready = session.players.length >= MAX_PLAYERS;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">Salon d'attente</h1>
        <p className="text-muted-foreground">Partage ce lien pour inviter quelqu'un.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Code de la partie</CardTitle>
          <CardDescription>Code court ou lien complet à partager.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center">
            <p className="text-5xl font-bold tracking-widest font-mono text-primary">
              {session.shortCode}
            </p>
          </div>
          <div className="flex gap-2">
            <code className="flex-1 truncate rounded-md border border-input bg-input/40 px-3 py-2 text-sm">
              {joinUrl}
            </code>
            <Button
              variant="secondary"
              size="icon"
              onClick={copyLink}
              aria-label={copied ? 'Lien copié' : 'Copier le lien de la partie'}
              className="min-h-[44px] min-w-[44px]"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Joueurs ({session.players.length}/{MAX_PLAYERS})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <AnimatePresence initial={false}>
            {session.players.map((p) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, scale: 0.95, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.32, ease: 'easeOut' }}
                className="flex items-center justify-between rounded-md border border-border bg-card/40 px-3 py-2"
              >
                <span className="font-medium">{p.pseudo}</span>
                <span className="text-xs text-muted-foreground">intensité {p.intensity}</span>
              </motion.div>
            ))}
          </AnimatePresence>
          {Array.from({ length: MAX_PLAYERS - session.players.length }).map((_, i) => (
            <div
              key={`empty-${i}`}
              className="rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground italic"
            >
              En attente...
            </div>
          ))}
        </CardContent>
      </Card>

      {ready ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <Card className="border-primary">
            <CardContent className="pt-6 space-y-3 text-center">
              <p className="text-primary font-semibold">Tout le monde est là !</p>
              <p className="text-sm text-muted-foreground">
                Intensité : {session.intensityMin} | Sex : {session.optInSex ? 'OK' : 'off'} |
                Émotion : {session.optInEmotion ? 'OK' : 'off'} | Dare :{' '}
                {session.optInDare ? 'OK' : 'off'}
              </p>
              <Button size="lg" className="w-full min-h-[44px]" onClick={onStart}>
                Lancer la partie
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <p className="text-center text-sm text-muted-foreground animate-pulse">
          En attente du {MAX_PLAYERS - session.players.length === 1 ? '2e' : 'prochain'} joueur...
        </p>
      )}
    </div>
  );
}
